import React from 'react';
import { Card, CardContent, Box, Typography, Grid } from '@mui/material';
import { Shield } from 'lucide-react';

const SniffrBenefitsCard = ({ stats, subscription, issueTrends }) => {
  const benefits = [
    {
      icon: '⏱️',
      percentage: '8.2%',
      value: '10,656h',
      title: 'Review Time Saved',
      description: 'Saved thousands of engineering hours — reclaimed for feature delivery',
      color: '#0e7490',
      gradientFrom: '#f0f9ff',
      gradientTo: '#e0f2fe',
      borderColor: '#bae6fd',
      trend: 'up',
      isGoodTrend: true
    },
    {
      icon: '📋',
      percentage: '14.3%',
      value: '120',
      title: 'Requirement Gaps Flagged',
      description: 'Identified missed business requirements early — reducing costly rework',
      color: '#7c3aed',
      gradientFrom: '#faf5ff',
      gradientTo: '#f3e8ff',
      borderColor: '#d8b4fe',
      trend: 'up',
      isGoodTrend: true
    },
    {
      icon: '🧪',
      percentage: '7.2%',
      value: '7,720',
      title: 'Tests Executed Automatically',
      description: 'Thousands of tests executed automatically — giving leaders release confidence',
      color: '#ea580c',
      gradientFrom: '#fff7ed',
      gradientTo: '#fed7aa',
      borderColor: '#fdba74',
      trend: 'up',
      isGoodTrend: true
    }
  ];

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #faf5ff 60%, #fff7ed 100%)',
        border: '1px solid #bae6fd',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        height: '100%',
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      <CardContent sx={{ px: 5, pb: 5 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'left', mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 60%, #ea580c 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 0.5,
              color: '#fff',
              p: 1.5
            }}
          >
            <Shield />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', alignItems: 'start', gap: 0.5 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#020817',
                fontSize: '1.125rem',
                letterSpacing: '-0.025em',
                lineHeight: '1.75rem',
              }}
            >
              How Sniffr.ai Benefits Your Team
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#6b7280',
                fontWeight: 400,
                fontSize: '0.75rem',
                letterSpacing: '-0.025em',
                lineHeight: '1rem',
              }}
            >
              ROI from Sniffr.ai (Executive View)
            </Typography>
          </Box>
        </Box>

        {/* Benefits Cards */}
        <Grid container spacing={2}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} md={4} xl={4} key={index}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: '8px',
                  height: '100%',
                  background: `linear-gradient(135deg, ${benefit.gradientFrom} 0%, ${benefit.gradientTo} 100%)`,
                  border: `0.5px solid ${benefit.borderColor}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', mb: 2 }}>
                  <Box sx={{ fontSize: '1.5rem' }}>
                    {benefit.icon}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.2,
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: benefit.isGoodTrend ? '#166534' : '#991b1b'
                    }}
                  >
                    {benefit.trend === "up" ? "↗" : "↘"} {benefit.percentage}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.5rem', md: '1.25rem', sm: '1.5rem' },
                      fontWeight: 700,
                      color: benefit.color
                    }}
                  >
                    {benefit.value}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: '0.75rem', md: '0.7rem', sm: '0.875rem' },
                      fontWeight: 600,
                      color: '#111827'
                    }}
                  >
                    {benefit.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: '0.675rem',
                      color: '#6b7280'
                    }}
                  >
                    {benefit.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SniffrBenefitsCard;
